// src/Components/Drawer/Markup/MarkupControls.js

import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Typography,
  TextField,
  CircularProgress,
  Switch,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { MenuEraser } from '../../../assets/icons/CustomIcons';
import { useIcons } from '../../../Contexts/IconContext';
import ColorPicker from '../../../utils/ColorPicker';

const DARK_BLUE = '#00008B';
const HIGHLIGHT_BLUE = '#00B0FF';

export default function MarkupControls({
  activeTool,
  drawingStyles,
  handleStyleChange,
  selectedMarker,
  mode,
  onPositionChange,
}) {
  const { markerIcons, loading } = useIcons();

  const [latInput, setLatInput] = useState('');
  const [lngInput, setLngInput] = useState('');

  // Listen for map clicks or dragging to automatically ingest coordinates into the inputs
  useEffect(() => {
    if (activeTool === 'MARKER' && selectedMarker && selectedMarker.position) {
      const p = selectedMarker.position;
      const lat = typeof p.lat === 'function' ? p.lat() : p.lat;
      const lng = typeof p.lng === 'function' ? p.lng() : p.lng;
      if (lat !== undefined && lng !== undefined) {
        setLatInput(Number(lat).toString());
        setLngInput(Number(lng).toString());
      }
    } else if (!selectedMarker) {
      setLatInput('');
      setLngInput('');
    }
  }, [selectedMarker, activeTool]);

  // Parse the user's manual coordinate inputs, pass them up to state, and fly the camera over to them
  const handleApplyCoords = () => {
    const lat = parseFloat(latInput);
    const lng = parseFloat(lngInput);
    if (!isNaN(lat) && !isNaN(lng)) {
      const newPos = { lat, lng };
      if (onPositionChange) onPositionChange(newPos);
      if (window.__loktrMapInstance) {
        window.__loktrMapInstance.panTo(newPos);
      }
    }
  };

  const handleClearCoords = () => {
    setLatInput('');
    setLngInput('');
  };

  const containerStyle = {
    minHeight: '70px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px', // AMENDED: Restored to original 12px
    backgroundColor: '#fff',
    borderBottom: '1px solid #e0e0e0',
    flexShrink: 0,
    zIndex: 20,
    position: 'relative',
    padding: '10px 10px',
    flexWrap: 'wrap',
  };

  const imgStyle = {
    width: '32px',
    height: '32px',
    objectFit: 'contain',
    display: 'block',
  };

  const labelStyle = {
    color: DARK_BLUE,
    fontWeight: 'bold',
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    marginBottom: '2px',
  };

  if (activeTool === 'MARKER') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        {/* ICON TRAY */}
        <Box sx={containerStyle}>
          {loading ? (
            <CircularProgress size={24} sx={{ color: DARK_BLUE }} />
          ) : (
            markerIcons
              .filter(
                (iconData) =>
                  iconData.icon && iconData.icon !== '' && !iconData.isArchived,
              )
              .map((iconData) => {
                const isSelected = drawingStyles.iconID
                  ? String(drawingStyles.iconID) === String(iconData.id)
                  : drawingStyles.markerIcon === iconData.icon;

                return (
                  <Box
                    key={iconData.id}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '4px',
                    }}>
                    <IconButton
                      sx={{
                        p: '6px',
                        borderRadius: '4px',
                        border: isSelected
                          ? `2px solid ${HIGHLIGHT_BLUE}`
                          : '2px solid transparent',
                        backgroundColor: isSelected ? '#E3F2FD' : 'transparent',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: isSelected ? '#E3F2FD' : '#f5f5f5',
                        },
                      }}
                      onClick={() => {
                        handleStyleChange({
                          markerIcon: iconData.icon,
                          iconID: iconData.id,
                        });
                      }}>
                      <img src={iconData.icon} alt={iconData.name} style={imgStyle} />
                    </IconButton>

                    <Typography
                      variant='caption'
                      sx={{
                        fontSize: '0.7rem',
                        color: isSelected ? DARK_BLUE : '#666',
                        fontWeight: isSelected ? 'bold' : 'normal',
                        lineHeight: 1.2,
                        minHeight: '1.2em', // AMENDED: Force bounding box to prevent grid collapse
                        display: 'flex',
                        alignItems: 'center',
                      }}>
                      {iconData.name || '\u00A0'}
                    </Typography>
                  </Box>
                );
              })
          )}
        </Box>

        {/* STATIC LAT/LNG PANEL */}
        <Box sx={{ p: 1, backgroundColor: '#fff', borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1, width: '100%' }}>
            {/* LAT / LNG WRAPPER BOX */}
            <Box
              sx={{
                flexGrow: 1,
                border: `1px solid ${HIGHLIGHT_BLUE}`,
                borderRadius: '4px',
                p: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                backgroundColor: '#fdfdfd',
              }}>
              {/* LAT ROW */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ ...labelStyle, width: '25px', mb: 0 }}>LAT</Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexGrow: 1,
                    border: '1px solid #ccc',
                    borderRadius: '3px',
                    px: 0.5,
                    py: '2px',
                    backgroundColor: '#fff',
                  }}>
                  <input
                    type='text'
                    placeholder='Enter Lat'
                    value={latInput}
                    onChange={(e) => setLatInput(e.target.value)}
                    style={{
                      border: 'none',
                      outline: 'none',
                      width: '100%',
                      color: DARK_BLUE,
                      fontSize: '0.8rem',
                      backgroundColor: 'transparent',
                    }}
                  />
                </Box>
              </Box>

              {/* LNG ROW */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ ...labelStyle, width: '25px', mb: 0 }}>LNG</Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexGrow: 1,
                    border: '1px solid #ccc',
                    borderRadius: '3px',
                    px: 0.5,
                    py: '2px',
                    backgroundColor: '#fff',
                  }}>
                  <input
                    type='text'
                    placeholder='Enter Lng'
                    value={lngInput}
                    onChange={(e) => setLngInput(e.target.value)}
                    style={{
                      border: 'none',
                      outline: 'none',
                      width: '100%',
                      color: DARK_BLUE,
                      fontSize: '0.8rem',
                      backgroundColor: 'transparent',
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* BUTTONS COLUMN */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: 1,
              }}>
              <IconButton
                size='small'
                title='Apply Coordinate'
                onClick={handleApplyCoords}
                sx={{
                  p: '4px',
                  border: `1px solid ${HIGHLIGHT_BLUE}`,
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                  height: '28px',
                  width: '28px',
                  '&:hover': { backgroundColor: '#f0f0f0' },
                }}>
                <CheckCircleIcon sx={{ fontSize: '18px', color: '#4caf50' }} />
              </IconButton>

              <IconButton
                size='small'
                title='Clear Coordinates'
                onClick={handleClearCoords}
                sx={{
                  p: '4px',
                  border: `1px solid ${HIGHLIGHT_BLUE}`,
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                  height: '28px',
                  width: '28px',
                  '&:hover': { backgroundColor: '#f0f0f0' },
                }}>
                <MenuEraser sx={{ fontSize: '18px' }} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  if (activeTool === 'POLYGON') {
    return (
      <Box sx={containerStyle}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant='caption' sx={labelStyle}>
            FILL COLOR:
          </Typography>

          <ColorPicker
            value={drawingStyles.polygonColor || '#FF9900'}
            onChange={(color) => handleStyleChange({ polygonColor: color })}
            label='Select Polygon Color'
          />
        </Box>
      </Box>
    );
  }

  if (activeTool === 'LINE') {
    return (
      <Box sx={containerStyle}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '40px 140px 100px',
            rowGap: '12px',
            columnGap: '16px',
            alignItems: 'flex-end',
          }}>
          {/* --- ROW 1: BASE LINE --- */}
          <Box /> {/* Empty cell for alignment */}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant='caption' sx={labelStyle}>
              BASE COLOR:
            </Typography>
            <ColorPicker
              value={drawingStyles.lineColor || '#00B0FF'}
              onChange={(color) => handleStyleChange({ lineColor: color })}
              label='Select Line Color'
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant='caption' sx={labelStyle}>
              WIDTH:
            </Typography>
            <TextField
              type='number'
              variant='outlined'
              size='small'
              value={drawingStyles.lineWeight || 4}
              onChange={(e) => handleStyleChange({ lineWeight: Number(e.target.value) })}
              slotProps={{
                htmlInput: {
                  min: 1,
                  max: 20,
                  style: {
                    padding: '4px',
                    textAlign: 'center',
                    color: DARK_BLUE,
                    fontWeight: 'bold',
                  },
                },
              }}
              sx={{
                width: '60px',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: HIGHLIGHT_BLUE },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: HIGHLIGHT_BLUE,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: HIGHLIGHT_BLUE,
                },
              }}
            />
          </Box>
          {/* --- ROW 2: DASHED LINE OVERLAY --- */}
          <Box sx={{ display: 'flex', justifyContent: 'center', pb: '6px' }}>
            <Switch
              size='small'
              checked={drawingStyles.isDashed || false}
              onChange={(e) => handleStyleChange({ isDashed: e.target.checked })}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#666',
                  '& + .MuiSwitch-track': {
                    backgroundColor: '#999',
                  },
                },
              }}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              opacity: drawingStyles.isDashed ? 1 : 0.4,
              pointerEvents: drawingStyles.isDashed ? 'auto' : 'none',
              transition: 'opacity 0.2s',
            }}>
            <Typography variant='caption' sx={labelStyle}>
              DASH COLOR:
            </Typography>
            <ColorPicker
              value={drawingStyles.dashColor || '#000000'}
              onChange={(color) => handleStyleChange({ dashColor: color })}
              label='Select Dash Color'
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              opacity: drawingStyles.isDashed ? 1 : 0.4,
              pointerEvents: drawingStyles.isDashed ? 'auto' : 'none',
              transition: 'opacity 0.2s',
            }}>
            <Typography variant='caption' sx={labelStyle}>
              LENGTH:
            </Typography>
            <TextField
              type='number'
              variant='outlined'
              size='small'
              disabled={!drawingStyles.isDashed}
              value={drawingStyles.dashLength || 10}
              onChange={(e) => handleStyleChange({ dashLength: Number(e.target.value) })}
              slotProps={{
                htmlInput: {
                  min: 1,
                  max: 100,
                  style: {
                    padding: '4px',
                    textAlign: 'center',
                    color: DARK_BLUE,
                    fontWeight: 'bold',
                  },
                },
              }}
              sx={{
                width: '60px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: drawingStyles.isDashed ? HIGHLIGHT_BLUE : '#ccc',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: drawingStyles.isDashed ? HIGHLIGHT_BLUE : '#ccc',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: drawingStyles.isDashed ? HIGHLIGHT_BLUE : '#ccc',
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  return null;
}
